import axios from 'axios';

// Always use relative paths — proxy handles routing in both dev (Vite) and production (Express server.js)
export const INTEGRATED_BASE_URL = '/api-integrated';
export const RINJANI_BASE_URL = '/api-rinjani';

const API_BASE_URL = RINJANI_BASE_URL;
const USER_MEMBER_API_BASE_URL = `${INTEGRATED_BASE_URL}/usermember`;
const EMPLOYEE_API_BASE_URL = `${INTEGRATED_BASE_URL}/employee`;
const HOTEL_API_BASE_URL = `${INTEGRATED_BASE_URL}/hotel`;
const HOTEL_IMAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/hotelimage`;
const LOUNGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/lounge`;
const LOUNGE_IMAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/loungeimage`;
const CONCIERGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/concierge`;
const CONCIERGE_IMAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/conciergeimage`;
const BAGGAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/baggage`;
const BAGGAGE_IMAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/baggageimage`;
const AIRPORT_API_BASE_URL = `${INTEGRATED_BASE_URL}/airport`;
const IEEM_PUBLISH_API_BASE_URL = `${INTEGRATED_BASE_URL}/ieemPublish`;
const IEEM_FLYER_API_BASE_URL = `${INTEGRATED_BASE_URL}/ieemflyer`;
const IEEM_IFRAME_API_BASE_URL = `${INTEGRATED_BASE_URL}/ieemiframe`;
const BP_PRODUCT_API_BASE_URL = `${INTEGRATED_BASE_URL}/bpproduct`;
const BP_PRODUCT_NEW_API_BASE_URL = `${INTEGRATED_BASE_URL}/bpproductnew`;
const BP_PRODUCT_IMAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/bpproductimage`;
const BP_PRODUCT_RETAIL_API_BASE_URL = `${INTEGRATED_BASE_URL}/bpproductretail`;
const USER_ACTIVITY_LOG_API_BASE_URL = `${INTEGRATED_BASE_URL}/useractivitylog`;
const MENU_CATEGORY_API_BASE_URL = `${INTEGRATED_BASE_URL}/menucategory`;
const MENU_SUBCATEGORY_API_BASE_URL = `${INTEGRATED_BASE_URL}/menusubcategory`;
const MENU_API_BASE_URL = `${INTEGRATED_BASE_URL}/menu`;
const USER_MENU_API_BASE_URL = `${INTEGRATED_BASE_URL}/usermenu`;
const HOTEL_ROOMTYPE_API_BASE_URL = `${INTEGRATED_BASE_URL}/hotelroomtype`;
const PACKAGE_API_BASE_URL = `${INTEGRATED_BASE_URL}/package`;
const PRODUCT_RETAIL_TRANSACTION_API_BASE_URL = `${INTEGRATED_BASE_URL}/productretailtransaction`;
const PRODUCT_SERVICES_API_BASE_URL = `${INTEGRATED_BASE_URL}/productservices`;
const HOSPITALITY_API_BASE_URL = `${INTEGRATED_BASE_URL}/hospitality`;
const IDM_API_BASE_URL = `${INTEGRATED_BASE_URL}/idm`;
const IAS_API_BASE_URL = `${INTEGRATED_BASE_URL}/ias`;
const CHANNEL_API_BASE_URL = `${INTEGRATED_BASE_URL}/channel`;
const GOERS_API_BASE_URL = `${INTEGRATED_BASE_URL}/goers`;
const SARINAH_API_BASE_URL = `${INTEGRATED_BASE_URL}/sarinah`;
const PRODUCT_MONITORING_API_BASE_URL = `${INTEGRATED_BASE_URL}/product-monitoring`;

// Create axios instance for login API
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for userMember API
export const userMemberApi = axios.create({
  baseURL: USER_MEMBER_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for employee API
export const employeeApi = axios.create({
  baseURL: EMPLOYEE_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for hotel API
export const hotelApi = axios.create({
  baseURL: HOTEL_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for hotel image API
export const hotelImageApi = axios.create({
  baseURL: HOTEL_IMAGE_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Create axios instance for lounge API
export const loungeApi = axios.create({
  baseURL: LOUNGE_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for lounge image API
export const loungeImageApi = axios.create({
  baseURL: LOUNGE_IMAGE_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Create axios instance for concierge API
export const conciergeApi = axios.create({
  baseURL: CONCIERGE_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for concierge image API
export const conciergeImageApi = axios.create({
  baseURL: CONCIERGE_IMAGE_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Create axios instance for baggage API
export const baggageApi = axios.create({
  baseURL: BAGGAGE_API_BASE_URL,
  timeout: 10000,
});

// Create axios instance for baggage image API
export const baggageImageApi = axios.create({
  baseURL: BAGGAGE_IMAGE_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Create axios instance for airport API
export const airportApi = axios.create({
  baseURL: AIRPORT_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('integratedToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('isAuthenticated');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Centralized 401 handler — auto-logout + redirect for all integrated API instances.
// Applied to every instance used after login to ensure consistent session-expiry UX.
const add401Handler = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('integratedToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('isAuthenticated');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Request interceptor for userMember API to add token
// Menggunakan integratedToken untuk API integrated.injourney.id
userMemberApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for userMember API
userMemberApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Jangan langsung logout jika error 401
    // Biarkan component yang handle error tersebut
    // Hanya logout jika benar-benar token invalid dan user sudah login
    if (error.response?.status === 401) {
      // Cek apakah ini error karena token tidak ada atau token invalid
      const integratedToken = localStorage.getItem('integratedToken');
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      
      // Jika token tidak ada tapi user sudah login, mungkin token belum di-generate
      // Jangan logout, biarkan error di-handle oleh component
      if (!integratedToken && isAuthenticated) {
        console.warn('Integrated token not found, but user is authenticated. Error will be handled by component.');
        return Promise.reject(error);
      }
      
      // Jika token ada tapi masih 401, mungkin token expired atau invalid
      // Tapi jangan langsung logout, biarkan component handle
      console.warn('401 Unauthorized from integrated API. Error will be handled by component.');
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// Request interceptor for employee API to add token
employeeApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for employee API
employeeApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for hotel API to add token
hotelApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for hotel API
hotelApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for IEEM Publish API
export const ieemPublishApi = axios.create({
  baseURL: IEEM_PUBLISH_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for IEEM Publish API to add token
ieemPublishApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for IEEM Publish API
ieemPublishApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for hotel image API to add token
hotelImageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for hotel image API
hotelImageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for lounge API to add token
loungeApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for lounge API
loungeApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for lounge image API to add token
loungeImageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for lounge image API
loungeImageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for airport API to add token
airportApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for airport API
airportApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for concierge API to add token
conciergeApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for concierge API
conciergeApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for concierge image API to add token
conciergeImageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for concierge image API
conciergeImageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for baggage API to add token
baggageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for baggage API
baggageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Request interceptor for baggage image API to add token
baggageImageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for baggage image API
baggageImageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for IEEM Flyer API
export const ieemFlyerApi = axios.create({
  baseURL: IEEM_FLYER_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Request interceptor for IEEM Flyer API to add token
ieemFlyerApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for IEEM Flyer API
ieemFlyerApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for IEEM Iframe API
export const ieemIframeApi = axios.create({
  baseURL: IEEM_IFRAME_API_BASE_URL,
  timeout: 30000, // Longer timeout for file uploads
});

// Request interceptor for IEEM Iframe API to add token
ieemIframeApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for IEEM Iframe API
ieemIframeApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for BP Product API
export const bpProductApi = axios.create({
  baseURL: BP_PRODUCT_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for BP Product API to add token
bpProductApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for BP Product API
bpProductApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for BP Product Retail API
export const bpProductRetailApi = axios.create({
  baseURL: BP_PRODUCT_RETAIL_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for BP Product Retail API to add token
bpProductRetailApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for BP Product Retail API
bpProductRetailApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for BP Product New API
export const bpProductNewApi = axios.create({
  baseURL: BP_PRODUCT_NEW_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for BP Product New API to add token
bpProductNewApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for BP Product New API
bpProductNewApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for BP Product Image API
export const bpProductImageApi = axios.create({
  baseURL: BP_PRODUCT_IMAGE_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for BP Product Image API to add token
bpProductImageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for BP Product Image API
bpProductImageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for user activity log API
export const userActivityLogApi = axios.create({
  baseURL: USER_ACTIVITY_LOG_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for user activity log API to add token
userActivityLogApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for user activity log API
userActivityLogApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for menu category API
export const menuCategoryApi = axios.create({
  baseURL: MENU_CATEGORY_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for menu category API to add token
menuCategoryApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for menu category API
menuCategoryApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for menu subcategory API
export const menuSubCategoryApi = axios.create({
  baseURL: MENU_SUBCATEGORY_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for menu subcategory API to add token
menuSubCategoryApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for menu subcategory API
menuSubCategoryApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for menu API
export const menuApi = axios.create({
  baseURL: MENU_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for menu API to add token
menuApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for menu API
menuApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for user menu API
export const userMenuApi = axios.create({
  baseURL: USER_MENU_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for user menu API to add token
userMenuApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for user menu API
userMenuApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for hotel room type API
export const hotelRoomTypeApi = axios.create({
  baseURL: HOTEL_ROOMTYPE_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for hotel room type API to add token
hotelRoomTypeApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for hotel room type API
hotelRoomTypeApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for Package API
export const packageApi = axios.create({
  baseURL: PACKAGE_API_BASE_URL,
  timeout: 10000,
});

// Request interceptor for Package API to add token
packageApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for Package API
packageApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for Product Retail Transaction API
export const productRetailTransactionApi = axios.create({
  baseURL: PRODUCT_RETAIL_TRANSACTION_API_BASE_URL,
  timeout: 60000, // 60 seconds for sync operations
});

// Request interceptor for Product Retail Transaction API to add token
productRetailTransactionApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for Product Retail Transaction API
productRetailTransactionApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for Product Services API
export const productServicesApi = axios.create({
  baseURL: PRODUCT_SERVICES_API_BASE_URL,
  timeout: 60000, // 60 seconds for sync operations
});

// Request interceptor for Product Services API to add token
productServicesApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for Product Services API
productServicesApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle error tanpa auto logout
    return Promise.reject(error);
  }
);

// Create axios instance for Hospitality API (Integration Data / FTP)
export const hospitalityApi = axios.create({
  baseURL: HOSPITALITY_API_BASE_URL,
  timeout: 30000,
});

// Request interceptor for Hospitality API to add token
hospitalityApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for Hospitality API
hospitalityApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create axios instance for IDM API integration
export const idmApi = axios.create({
  baseURL: IDM_API_BASE_URL,
  timeout: 30000,
});

// Create axios instance for IAS API integration
export const iasApi = axios.create({
  baseURL: IAS_API_BASE_URL,
  timeout: 30000,
});

// Request interceptor for IDM API to add token
idmApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for IDM API
idmApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for IAS API to add token
iasApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for IAS API
iasApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create axios instance for Channel API integration
export const channelApi = axios.create({
  baseURL: CHANNEL_API_BASE_URL,
  // Simulator email + PDF generation regularly exceeds 10s (SMTP round-trip).
  timeout: 60000,
});

channelApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

channelApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create axios instance for GOERS API
export const goersApi = axios.create({
  baseURL: GOERS_API_BASE_URL,
  timeout: 30000,
});

goersApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

goersApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Create axios instance for Sarinah API
export const sarinahApi = axios.create({
  baseURL: SARINAH_API_BASE_URL,
  timeout: 30000,
});

sarinahApi.interceptors.request.use(
  (config) => {
    const integratedToken = localStorage.getItem('integratedToken');
    if (integratedToken) {
      config.headers.Authorization = `Bearer ${integratedToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

sarinahApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Create axios instance for Product Monitoring API
export const productMonitoringApi = axios.create({ baseURL: PRODUCT_MONITORING_API_BASE_URL, timeout: 30000 });
productMonitoringApi.interceptors.request.use((config) => { const token = localStorage.getItem('integratedToken'); if (token) config.headers.Authorization = `Bearer ${token}`; return config; }, (e) => Promise.reject(e));
productMonitoringApi.interceptors.response.use((r) => r, (e) => Promise.reject(e));

// OpenAPI / Swagger documentation endpoints (root of integrated API)
export const openapiApi = axios.create({
  baseURL: INTEGRATED_BASE_URL,
  timeout: 60000,
});
openapiApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('integratedToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (e) => Promise.reject(e));
openapiApi.interceptors.response.use((r) => r, (e) => Promise.reject(e));

// Apply centralized 401 handler to all post-login API instances.
// userMemberApi is intentionally excluded — it has special handling during login flow.
[
  employeeApi, hotelApi, hotelImageApi, loungeApi, loungeImageApi,
  conciergeApi, conciergeImageApi, baggageApi, baggageImageApi, airportApi,
  ieemPublishApi, ieemFlyerApi, ieemIframeApi,
  bpProductApi, bpProductRetailApi, bpProductNewApi, bpProductImageApi,
  userActivityLogApi, menuCategoryApi, menuSubCategoryApi, menuApi,
  hotelRoomTypeApi, packageApi, productRetailTransactionApi, productServicesApi,
  hospitalityApi, idmApi, iasApi, channelApi, goersApi, sarinahApi, productMonitoringApi,
  openapiApi,
].forEach(add401Handler);

export default api;

